import './index.css';

import { usePrefersReducedMotion } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  FALLBACK,
  GREETING,
  matchAnswer,
  SUGGESTED_QUESTIONS,
} from './knowledge';

// Simulated "thinking" window before an answer appears.
const TYPING_DELAY_MIN = 600;
const TYPING_DELAY_MAX = 1100;
// Short, near-instant delay when the visitor prefers reduced motion.
const TYPING_DELAY_REDUCED = 200;

const Spark = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    width="16"
    height="16"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2.5c.5 3.4 1.6 5 5 5.5-3.4.5-4.5 2.1-5 5.5-.5-3.4-1.6-5-5-5.5 3.4-.5 4.5-2.1 5-5.5Z"
      fill="currentColor"
    />
    <path
      d="M18.5 13c.25 1.6.8 2.4 2.5 2.7-1.7.3-2.25 1.1-2.5 2.8-.25-1.7-.8-2.5-2.5-2.8 1.7-.3 2.25-1.1 2.5-2.7Z"
      fill="currentColor"
      opacity="0.7"
    />
  </svg>
);

const randomDelay = () =>
  Math.round(TYPING_DELAY_MIN + Math.random() * (TYPING_DELAY_MAX - TYPING_DELAY_MIN));

let messageSeq = 0;
const nextMessageId = () => `m${++messageSeq}`;

/**
 * Ask my portfolio — a front-end-only, scripted AI assistant demo.
 *
 * No network or LLM call: answers come from a local keyword-scored knowledge
 * base (./knowledge.js). It renders as a fixed floating launcher that toggles
 * an accessible chat dialog with a simulated typing indicator.
 */
const AskAI = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimerRef = useRef(null);

  // Seed the greeting once, the first time the panel opens.
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ id: nextMessageId(), role: 'assistant', text: GREETING }]);
    }
  }, [isOpen, messages.length]);

  // Move focus into the input on open; clear any pending typing timer on close.
  useEffect(() => {
    if (isOpen) {
      const raf = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen]);

  // Auto-scroll to the newest message / typing indicator.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      block: 'end',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [messages, isTyping, prefersReducedMotion]);

  // Clear any in-flight timer on unmount.
  useEffect(
    () => () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    },
    []
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);
    setIsTyping(false);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    // Return focus to the launcher for keyboard users.
    requestAnimationFrame(() => launcherRef.current?.focus());
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const submitQuestion = useCallback(
    rawQuestion => {
      const question = (rawQuestion || '').trim();
      if (!question || isTyping) return;

      setMessages(prev => [
        ...prev,
        { id: nextMessageId(), role: 'user', text: question },
      ]);
      setDraft('');
      setIsTyping(true);

      const { answer } = matchAnswer(question);
      const safeAnswer = answer || FALLBACK;
      const delay = prefersReducedMotion ? TYPING_DELAY_REDUCED : randomDelay();

      typingTimerRef.current = setTimeout(() => {
        setMessages(prev => [
          ...prev,
          { id: nextMessageId(), role: 'assistant', text: safeAnswer },
        ]);
        setIsTyping(false);
        typingTimerRef.current = null;
      }, delay);
    },
    [isTyping, prefersReducedMotion]
  );

  // Open from elsewhere on the page (e.g. the AI section CTA) via a window
  // event. Opens the panel, focuses the input, and optionally submits a
  // question through the normal answer flow. Guarded for SSR / react-snap.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOpen = event => {
      setIsOpen(true);
      requestAnimationFrame(() => inputRef.current?.focus());
      const question = event?.detail?.question;
      if (question) submitQuestion(question);
    };

    window.addEventListener('askai:open', handleOpen);
    return () => window.removeEventListener('askai:open', handleOpen);
  }, [submitQuestion]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      submitQuestion(draft);
    },
    [draft, submitQuestion]
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePanel();
      }
    },
    [closePanel]
  );

  return (
    <div
      className="ask-ai"
      data-open={isOpen ? 'true' : 'false'}
      data-reduced={prefersReducedMotion ? 'true' : 'false'}
      onKeyDown={isOpen ? handleKeyDown : undefined}
    >
      {isOpen && (
        <section
          ref={panelRef}
          className="ask-ai__panel"
          role="dialog"
          aria-label="Ask my portfolio — demo assistant"
        >
          <header className="ask-ai__header">
            <div className="ask-ai__heading">
              <span className="ask-ai__avatar" aria-hidden="true">
                <Spark />
              </span>
              <h2 className="ask-ai__title">Ask my portfolio</h2>
              <span className="ask-ai__tag">AI · demo</span>
            </div>
            <button
              type="button"
              className="ask-ai__close"
              aria-label="Close"
              onClick={closePanel}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>

          <div
            className="ask-ai__messages"
            aria-live="polite"
            aria-atomic="false"
          >
            {messages.map(message => (
              <div
                key={message.id}
                className={`ask-ai__msg ask-ai__msg--${message.role}`}
              >
                {message.role === 'assistant' && (
                  <span className="ask-ai__msg-avatar" aria-hidden="true">
                    <Spark />
                  </span>
                )}
                <p className="ask-ai__bubble">{message.text}</p>
              </div>
            ))}

            {isTyping && (
              <div className="ask-ai__msg ask-ai__msg--assistant">
                <span className="ask-ai__msg-avatar" aria-hidden="true">
                  <Spark />
                </span>
                <p className="ask-ai__bubble ask-ai__bubble--typing">
                  <span className="ask-ai__sr">Assistant is typing…</span>
                  <span className="ask-ai__dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="ask-ai__suggestions">
            {SUGGESTED_QUESTIONS.map(question => (
              <button
                key={question}
                type="button"
                className="ask-ai__chip"
                onClick={() => submitQuestion(question)}
                disabled={isTyping}
              >
                {question}
              </button>
            ))}
          </div>

          <form className="ask-ai__form" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              className="ask-ai__input"
              placeholder="Ask about Sébastien…"
              aria-label="Ask about Sébastien"
              value={draft}
              onChange={event => setDraft(event.target.value)}
              autoComplete="off"
            />
            <button
              type="submit"
              className="ask-ai__send"
              aria-label="Send question"
              disabled={isTyping || !draft.trim()}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M4 12l16-8-6 8 6 8-16-8Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </form>
        </section>
      )}

      <button
        ref={launcherRef}
        type="button"
        className="ask-ai__launcher"
        aria-label={isOpen ? 'Close the demo assistant' : 'Ask my AI — demo assistant'}
        aria-expanded={isOpen}
        onClick={togglePanel}
      >
        <Spark className="ask-ai__launcher-spark" />
        <span className="ask-ai__launcher-label">Ask my AI</span>
      </button>
    </div>
  );
};

export default AskAI;
